import { useEffect } from 'react'

import {
  hoveredProductStore,
  selectedProductStore,
  cartOpenStore,
  watchOpenStore,
  cassaStore,
  vicinoCassaStore,
  cartStore,
  lightboxStore,
  useStore,
  isUiOpen,
  relockPointer,
  gumballStore,
  mirinoGumballStore,
  statoGumballStore,
} from '../../store/uiStore'
import { IS_TOUCH } from '../../config/touch'
import { avviaGiro, chiudiPremio, fraQuanto } from '../../config/gumball'

// ============================================================
// HUD 2D: mirino al centro + suggerimento "premi E" quando un
// prodotto è nel mirino. Gestisce anche i tasti globali:
//   E   → apre la pagina del prodotto inquadrato
//   C   → apre/chiude il carrello
//   Esc → chiude pagina prodotto / carrello
// ============================================================

export default function Hud() {
  const hovered = useStore(hoveredProductStore)
  const selected = useStore(selectedProductStore)
  const cartOpen = useStore(cartOpenStore)
  const mirinoGumball = useStore(mirinoGumballStore)
  const statoGumball = useStore(statoGumballStore)
  // gia' giocato da qui: la macchinetta si mira lo stesso, ma la scritta
  // dice quando si potra' rigiocare (anche in ?debug)
  const gumballInAttesa = statoGumball.prossima > Date.now()

  useEffect(() => {
    const onKeyDown = (e) => {
      // Mentre si scrive (campo di ricerca) i tasti singoli non devono
      // pilotare il gioco: senza questo, cercare "cocacola" aprirebbe il
      // carrello sulla c e l'orologio sulla q.
      const t = e.target
      if (t instanceof HTMLElement &&
          (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (e.code === 'KeyE') {
        // LA MACCHINETTA: col premio aperto E lo chiude, a giro in corso
        // non fa niente, e da ferma la avvia se e' nel mirino. Viene PRIMA
        // della cassa apposta: le due stanno sullo stesso bancone, ma la
        // cassa si attiva con la sola vicinanza mentre la macchinetta va
        // MIRATA — e mirarla vuol dire volere lei.
        const faseGumball = gumballStore.get()
        if (faseGumball === 'apre') { chiudiPremio(); return }
        if (faseGumball !== 'ferma') return
        if (mirinoGumballStore.get() && !hoveredProductStore.get() &&
            !selectedProductStore.get() && !isUiOpen()) {
          avviaGiro()
          return
        }
        // Alla cassa E avvia il pagamento, ma solo se non c'è un prodotto
        // nel mirino: quello ha la precedenza, altrimenti passando davanti
        // al bancone non si riuscirebbe più a guardare la merce.
        const carrello = cartStore.get()
        if (cassaStore.get() === 'ferma' && vicinoCassaStore.get() &&
            !hoveredProductStore.get() && !selectedProductStore.get() &&
            !isUiOpen() && (carrello?.totalQuantity || 0) > 0) {
          cassaStore.set('attesa')
          return
        }
        // E è un toggle. Prodotto in mano = mouse SEMPRE libero:
        // prenderlo esce dal pointer lock, rimetterlo lo riaggancia.
        if (selectedProductStore.get()) {
          lightboxStore.set(null)
          selectedProductStore.set(null)
          relockPointer()
          return
        }
        const product = hoveredProductStore.get()
        if (product && !isUiOpen()) {
          selectedProductStore.set(product)
          document.exitPointerLock?.()
        }
      }
      if (e.code === 'KeyC') {
        if (selectedProductStore.get()) return // pagina prodotto ha priorità
        const willOpen = !cartOpenStore.get()
        cartOpenStore.set(willOpen)
        if (willOpen) document.exitPointerLock?.()
        else relockPointer() // chiusura da tastiera → mouse subito riagganciato
      }
      if (e.code === 'KeyQ') {
        // L'orologio è un toggle. Il mouse si libera per poter toccare
        // le app sul quadrante, e si riaggancia quando si abbassa.
        if (selectedProductStore.get() || cartOpenStore.get()) return
        const willOpen = !watchOpenStore.get()
        watchOpenStore.set(willOpen)
        if (willOpen) document.exitPointerLock?.()
        else relockPointer()
      }
      if (e.code === 'Escape') {
        const wasOpen = isUiOpen()
        selectedProductStore.set(null)
        cartOpenStore.set(false)
        watchOpenStore.set(false)
        cassaStore.set('ferma')
        gumballStore.set('ferma')
        // solo se c'era una UI aperta: l'Esc "nudo" è il gesto nativo
        // del browser per uscire dal pointer lock, non va contrastato
        if (wasOpen) relockPointer()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Il mirino resta anche col prodotto in mano (si è ancora in FPS);
  // sparisce solo col carrello aperto
  if (cartOpen) return null

  return (
    <div className="hud">
      {/* niente mirino col prodotto in mano */}
      {!selected && (
        <div className={hovered || mirinoGumball ? 'crosshair active' : 'crosshair'} />
      )}
      {mirinoGumball && !hovered && !selected && gumballInAttesa && (
        <div className="hud-hint">
          Hai già giocato — torna {fraQuanto(statoGumball.prossima)}
        </div>
      )}
      {/* spenta: si mira lo stesso, ma non si promette una giocata */}
      {mirinoGumball && !hovered && !selected && !gumballInAttesa && !statoGumball.funziona && (
        <div className="hud-hint">Macchinetta fuori servizio</div>
      )}
      {mirinoGumball && !hovered && !selected && !gumballInAttesa && statoGumball.funziona && !IS_TOUCH && (
        <div className="hud-hint">
          Premi <kbd>E</kbd> — gira la manopola
        </div>
      )}
      {mirinoGumball && !hovered && !selected && !gumballInAttesa && statoGumball.funziona && IS_TOUCH && (
        <button className="hud-action" onClick={avviaGiro}>
          Gira la manopola
        </button>
      )}
      {hovered && !selected && !IS_TOUCH && (
        <div className="hud-hint">
          Premi <kbd>E</kbd> — {hovered.title}
        </div>
      )}
      {/* Su touch niente tasto E: bottone tappabile per aprire il prodotto */}
      {hovered && !selected && IS_TOUCH && (
        <button
          className="hud-action"
          onClick={() => selectedProductStore.set(hoveredProductStore.get())}
        >
          Guarda — {hovered.title}
        </button>
      )}
    </div>
  )
}
